import React, { useState } from 'react';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useNavigate } from 'react-router-dom';
import { Label, MessageBarBody, MessageBarTitle, Spinner, Text } from '@fluentui/react-components';
import { ArrowImport16Regular, Open16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideLinkButton } from '../../../../theme/components/GuideButtons';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { StatusPill } from '../../../../theme/components/StatusPill';
import { AppState } from '../../../../redux/reducers';
import { actionCreators, SourceProviderInfo } from '../../../../redux/middleware/ProviderConsumptionImport.Middleware';
import { buildImportedConsumptionsRoute, getApiErrorMessage } from '../../../../functions/ProviderConsumptionImport.Functions';
import './ConsumptionSourceProvider.Styles.css';

export interface ConsumptionSourceProviderOwnProps {
  /** Guid del consumo de proveedor del que se generó el consumo de protocolo */
  sourceProviderConsumptionId: string;
  /** Guid del proveedor (person.personIdentifier) que trae el consumo de protocolo */
  providerIdentifier?: string | null;
  providerName?: string | null;
}

interface DispatchProps {
  ResolveSourceProvider: (
    sourceProviderConsumptionId: string,
    providerIdentifier?: string | null,
    providerName?: string | null
  ) => Promise<SourceProviderInfo>;
}

export type ConsumptionSourceProviderProps = ConsumptionSourceProviderOwnProps & DispatchProps;

/**
 * "Origen: consumo de proveedor" (spec 005, US8) en el detalle del consumo de protocolo: muestra el proveedor y abre su
 * detalle en la solapa "Consumos importados". El id int de la ruta se resuelve al pulsar (el consumo sólo trae el Guid).
 */
export const ConsumptionSourceProvider = (props: ConsumptionSourceProviderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const providerName = props.providerName?.trim() || '';

  const openImportedConsumptions = async () => {
    if (resolving) return;
    setResolving(true);
    setError(undefined);
    try {
      const info = await props.ResolveSourceProvider(props.sourceProviderConsumptionId, props.providerIdentifier, props.providerName);
      if (info.providerId === undefined) {
        setError(t('consumptionDetails.sourceProvider.notFound'));
        return;
      }
      navigate(buildImportedConsumptionsRoute(info.providerId));
    } catch (failure: unknown) {
      setError(getApiErrorMessage(failure) || t('consumptionDetails.sourceProvider.notFound'));
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="infoField consumption-source-provider" data-testid="consumption-source-provider">
      <Label>{t('consumptionDetails.sourceProvider.label')}</Label>
      <div className="consumption-source-provider__row">
        <StatusPill tone="confirmed" icon={<ArrowImport16Regular />}>
          {t('consumptionDetails.sourceProvider.origin')}
        </StatusPill>
        {providerName && <Text className="consumption-source-provider__name">{providerName}</Text>}
        <GuideLinkButton
          icon={resolving ? <Spinner size="extra-tiny" /> : <Open16Regular />}
          onClick={openImportedConsumptions}
          disabled={resolving}
          data-testid="consumption-source-provider-open"
        >
          {t('consumptionDetails.sourceProvider.open')}
        </GuideLinkButton>
      </div>
      {error && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="consumption-source-provider-error">
          <MessageBarBody>
            <MessageBarTitle>{t('consumptionDetails.sourceProvider.errorTitle')}</MessageBarTitle>
            {error}
          </MessageBarBody>
        </GuideMessageBar>
      )}
    </div>
  );
};

export const mapDispatchToProps = (dispatch: ThunkDispatch<AppState, unknown, AnyAction>): DispatchProps => ({
  ResolveSourceProvider: (id, identifier, name) =>
    dispatch(actionCreators.ResolveSourceProvider(id, identifier, name) as any) as unknown as Promise<SourceProviderInfo>,
});

export default connect(null, mapDispatchToProps)(ConsumptionSourceProvider);
